import { Injectable } from "@angular/core";
import { HttpClient, HttpContext } from "@angular/common/http";
import { BehaviorSubject, catchError, of, tap } from "rxjs";
import { environment } from "src/environment/environment";
import { User } from "../models";
import { SILENT_401 } from "../interceptors/http-error.interceptor";

@Injectable({ providedIn: "root" })
export class AuthService {
  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  constructor(private http: HttpClient) {}

  // appelé au boot de l'app (ex: dans AppComponent.ngOnInit)
  hydrate() {
    return this.http
      .get<{ user: User }>(`${environment.apiBase}/auth/me`, {
        context: new HttpContext().set(SILENT_401, true),
        withCredentials: true,
      })
      .pipe(
        tap((res) => this.userSubject.next(res.user)),
        catchError(() => {
          this.userSubject.next(null);
          return of(null);
        })
      );
  }

  register(username: string, password: string) {
    return this.http.post<{ user: User }>(
      `${environment.apiBase}/auth/register`,
      { username, password },
      { withCredentials: true }
    );
  }

  login(username: string, password: string) {
    return this.http
      .post<{ user: User }>(
        `${environment.apiBase}/auth/login`,
        { username, password },
        { withCredentials: true }
      )
      .pipe(tap((res) => this.userSubject.next(res.user)));
  }

  logout() {
    return this.http
      .post(`${environment.apiBase}/auth/logout`, {}, { withCredentials: true })
      .pipe(tap(() => this.clearClientState()));
  }

  private clearClientState() {
    this.userSubject.next(null);
  }

  get currentUser() {
    return this.userSubject.value;
  }

  get isLoggedIn(): boolean {
    return !!this.userSubject.value;
  }
}
